<script setup>
import { onMounted, computed } from 'vue'
import EmptyState from '@/components/EmptyState.vue'

import Bar from '@/components/Bar_new.vue';
import PreviewHeader from '@/components/PreviewHeader.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import { ref } from 'vue';
import { useRouter } from 'vue-router';
const router = useRouter();
const showFullscreenLoading = ref(false);
async function handleWidgetNavigation(to) {
    showFullscreenLoading.value = true;
    try {
        await router.push(to);
    } finally {
        showFullscreenLoading.value = false;
    }
}
import InlineSpinner from '@/components/InlineSpinner.vue'

import { useUserStore } from '@/stores/user'
const userStore = useUserStore();

import { useActivityStore } from '@/stores/activity'
const activityStore = useActivityStore();

// Progress fraction (0-1) based on elapsed time/period progress (expired %) similar to performance widgets
// Uses progress.expired (0-100) if available; falls back to plannedCurrent/plannedTotal if expired missing
const progressFraction = computed(() => {
    const segment = userStore.currentSelection.activity.display;
    const expiredPercent = activityStore[segment]?.progress?.expired;
    if (typeof expiredPercent === 'number' && !isNaN(expiredPercent)) {
        // Kaufmännisch runden ("half up") auf ganze Prozentpunkte, dann in 0-1 umwandeln
        const rounded = Math.round(expiredPercent); // Math.round erfüllt kaufmännisches Runden
        return Math.min(Math.max(rounded, 0), 100) / 100; // clamp 0-100
    }
    // Fallback falls expired nicht vorhanden ist
    const current = activityStore[segment]?.progress?.plannedCurrent || 0;
    const total = activityStore[segment]?.progress?.plannedTotal || 1;
    if (total === 0) return 0;
    const fraction = Math.min(current / total, 1);
    const roundedPercent = Math.round(fraction * 100); // kaufmännisch runden
    return roundedPercent / 100;
});

console.log(progressFraction.value);

async function handleDisplayStatus(display) {
    await userStore.selectDisplayStatus('activity', display);
}

onMounted( () => {
    // ensure that the widgets data are always set to the current week and month
    for (const segment of ['week', 'month']) {
        if (activityStore.activity[segment]) {
            const currentEntry = activityStore.activity[segment]?.find((entry) => entry.isCurrent);
            userStore.currentSelection.activity[segment] = currentEntry ? currentEntry.id : null;
        }
    }
});

// Helper: prüft, ob alle Werte in einem Objekt 0 sind
function allValuesZero(obj) {
    if (!obj || typeof obj !== 'object') return true;
    return Object.values(obj).every(v => v === 0);
}

const isActivityZeroed = computed(() => {
    const week = activityStore.activity.week && activityStore.activity.week[0];
    const month = activityStore.activity.month && activityStore.activity.month[0];
    const weekZero = week && allValuesZero(week.actualValues) && allValuesZero(week.plannedValues);
    const monthZero = month && allValuesZero(month.actualValues) && allValuesZero(month.plannedValues);
    return weekZero || monthZero;
});
</script>

<template>
    <div class="activity-preview-block activity">
    <preview-header
        title="Aktivitäten"
        to="/activity"
        button-label="Zur Seite"
        :disabled="isActivityZeroed || userStore.currentSelection.activity.isLoading === true"
        @click="handleWidgetNavigation({ path: '/activity' })"
    />
    <LoadingOverlay :show="showFullscreenLoading" label="Daten werden geladen…" spinner-size="lg" />
  <div id="activity" :class="['widget-wrapper activity', {'noValue' : !activityStore.week}]">
    <div class="container-wrapper">
        <template v-if="userStore.currentSelection.activity.isLoading === true">
            <!-- Skeleton structure with 0 values, spinner overlays -->
            <div class="container main-content">
                <div class="headline-wrapper activity-head">
                    <span class="headline col-title">Ergebnisse</span>
                    <span class="headline col-actual">Ist</span>
                    <span class="headline col-goal plannedValues">Ziel</span>
                </div>
                <div class="bars-wrapper activity">
                    <span class="target-line" style="width: 0%"></span>
                    <bar :class="'selling-time'" :mode="'performanceNormal'" title="Verkaufszeit" :currentValue="0" :goalValue="0" />
                    <div class="container">
                        <span class="target-line cell-grid" style="width: 12px"></span>
                        <bar title="Entscheider" :mode="'activityNormal'" :currentValue="0" :goalValue="0" />
                        <bar title="Termine (Akt.)" :mode="'activityNormal'" :currentValue="0" :goalValue="0" />
                        <bar title="Kunden" :mode="'activityNormal'" :currentValue="0" :goalValue="0" />
                        <bar title="Gesamt" :mode="'activityNormal'" :isEuro="true" :currentValue="0" :goalValue="0" />
                        <bar title="Neu-Kd." :mode="'activityNormal'" :isEuro="true" :currentValue="0" :goalValue="0" />
                    </div>
                </div>
            </div>
            <div class="activity-loading-overlay">
                <InlineSpinner size="lg" label="Daten werden geladen" />
                <div class="loadingDescription">Daten werden geladen</div>
            </div>
        </template>
        <template v-else-if="isActivityZeroed">
            <div class="container">
                <EmptyState tKey="no_activity" icon="activity" pulse size="sm" />
            </div>
        </template>
        <template v-else>
            <div class="container main-content">
                <div class="headline-wrapper activity-head">
                    <span class="headline col-title">Ergebnisse</span>
                    <span class="headline col-actual">Ist</span>
                    <span class="headline col-goal plannedValues">Ziel</span>
                </div>
                <div class="bars-wrapper activity">
                    <span :class="['target-line']" :style="{ width: `calc((100% - 24px) * ${progressFraction})` }"></span>
                    <bar :class="'selling-time'" :mode="'performanceNormal'"  title="Verkaufszeit" :currentValue="activityStore[userStore.currentSelection.activity.display].progress?.plannedCurrent" :goalValue="activityStore[userStore.currentSelection.activity.display].progress?.plannedTotal"></bar >
                    <div class="container"> 
                        <span :class="['target-line cell-grid']" :style="{ width: `calc(max(12px, 12px + ( (100% - 24px) * ${progressFraction} )))` }"></span>
                        <bar title="Entscheider" :mode="'activityNormal'" :currentValue="activityStore[userStore.currentSelection.activity.display].actualValues?.contacts" :goalValue="activityStore[userStore.currentSelection.activity.display].plannedValues?.contacts"></bar>
                        <bar title="Termine (Akt.)" :mode="'activityNormal'" :currentValue="activityStore[userStore.currentSelection.activity.display].actualValues?.appointmentsActivity" :goalValue="activityStore[userStore.currentSelection.activity.display].plannedValues?.appointments"></bar>
                        <bar title="Kunden" :mode="'activityNormal'" :currentValue="activityStore[userStore.currentSelection.activity.display].actualValues?.salesNumber" :goalValue="activityStore[userStore.currentSelection.activity.display].plannedValues?.salesNumber"></bar>
                        <bar v-if="userStore.currentSelection.activity.display === 'month' && userStore.currentSelection.campaign.id <= 22" title="Gesamt" :mode="'activityPoints'" :isEuro=true :currentValue="activityStore[userStore.currentSelection.activity.display].actualValues?.salesEuro" :goalValue="activityStore[userStore.currentSelection.activity.display].plannedValues?.salesEuro"></bar>
                        <bar v-else title="Gesamt" :mode="'activityNormal'" :isEuro=true :currentValue="activityStore[userStore.currentSelection.activity.display].actualValues?.salesEuro" :goalValue="activityStore[userStore.currentSelection.activity.display].plannedValues?.salesEuro"></bar>
                        <bar title="Neu-Kd." :mode="'activityNormal'" :isEuro=true :currentValue="activityStore[userStore.currentSelection.activity.display].actualValues?.salesEuroNewCustomers" :goalValue="activityStore[userStore.currentSelection.activity.display].plannedValues?.salesEuroNewCustomers"></bar>
                        <!-- <bar title="Storno" :currentValue="0" :goalValue="5323"></bar> -->
                    </div>
                </div>
            </div>
        </template>
        <div v-if="!activityStore.week && userStore.currentSelection.activity.isLoading === false" class="container">
            <EmptyState tKey="no_activity" icon="activity" pulse size="sm" />
        </div>
    </div>
    <div class='wave-wrapper'>
        <div class='wave -one'></div>
        <div class='wave -two'></div>
        <div class='wave -three'></div>
    </div>
    <div v-if="!isActivityZeroed" class="toggle-wrapper">
        <div class="toggle-option" @click="handleDisplayStatus('week')">
            <span class="toggle-label">Woche</span>
            <label class="switch">
                <input type="radio" name="activity-toggle" value="week" :checked="userStore.currentSelection.activity.display==='week'">
                <span class="custom-radio"></span>
            </label>
        </div>
        <div class="toggle-option" @click="handleDisplayStatus('month')">
            <span class="toggle-label">Monat</span>
            <label class="switch">
                <input type="radio" name="activity-toggle" value="month" :checked="userStore.currentSelection.activity.display==='month'">
                <span class="custom-radio"></span>
            </label>
        </div>
    </div>
</div>
</div>
</template>

<style scoped>
/* Headline alignment and unified styling similar to Potential & BK & Eigenrecherche */
.activity-preview-block .headline-wrapper.activity-head { display:flex; align-items:center; gap:1.2rem; /* horizontal padding adjusted for bar title alignment */ padding:0 .55rem .4rem; border-bottom:1px solid #2c353f; margin:0 0 .4rem; }
.activity-preview-block .headline-wrapper.activity-head .headline { font-size:.55rem; letter-spacing:.45px; text-transform:uppercase; font-weight:600; opacity:.75; position:relative; }
.activity-preview-block .headline-wrapper.activity-head .col-actual { width:52px; text-align:right; }
.activity-preview-block .headline-wrapper.activity-head .col-goal { width:48px; text-align:right; }
/* Light theme adjustments */
.theme--light .activity-preview-block .headline-wrapper.activity-head { border-color:#d5dde5; }
.theme--light .activity-preview-block .headline-wrapper.activity-head .headline { opacity:.8; }

/* Optionally emphasize goal column */
/* Goal column now uses orange accent */
.activity-preview-block .headline-wrapper.activity-head .col-goal { color:var(--sellwerk-accent-main,#d97706); }
.theme--light .activity-preview-block .headline-wrapper.activity-head .col-goal { color:var(--sellwerk-accent-main,#d97706); }
</style>

<style scoped>
.activity-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255,255,255,0.7);
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
</style>